// import OpenAI from 'openai';

// const openai = new OpenAI({
//   apiKey: import.meta.env.VITE_OPENAI_API_KEY,
//   dangerouslyAllowBrowser: true
// });

// export const generateChatResponse = async (messages: { role: 'user' | 'assistant'; content: string }[]) => {
//   try {
//     console.log('Generating chat response with messages:', messages);

//     const completion = await openai.chat.completions.create({
//       model: "gpt-3.5-turbo",
//       messages: [
//         {
//           role: "system",
//           content: "You are WedAI, a helpful and knowledgeable wedding planning assistant. You provide detailed, practical advice about wedding planning, always maintaining a friendly and supportive tone. You have expertise in budgeting, venue selection, timeline planning, vendor coordination, and all aspects of wedding organization."
//         },
//         ...messages
//       ],
//       temperature: 0.7,
//       max_tokens: 500
//     });

//     console.log('OpenAI response:', completion);

//     return completion.choices[0]?.message?.content || '';
//   } catch (error: any) {
//     console.error('OpenAI API Error:', error);
//     throw error;
//   }
// };

import WedifyApiRequest from '../../utils/wedify_api_request.js';

interface BackendChatResponse {
  message: string;
  success: boolean;
  data?: any;
  actionPerformed?: string;
  actionParameters?: any;
  actionResults?: any;
  totalActionsCompleted?: number;
  totalActionsRequested?: number;
}

interface ChatResponse {
  text: string;
  message?: string; // Alternative text field from backend
  actions?: Array<{
    type: 'calendar' | 'task' | 'budget' | 'vendor';
    data: any;
  }>;
  data?: any; // Include backend data for search results
  actionPerformed?: string; // New backend format
  actionParameters?: {
    page?: string;
    reason?: string;
    [key: string]: any;
  }; // New backend format parameters
}

export const generateChatResponse = async (text: string): Promise<ChatResponse> => {
  try {
    console.log('🌐 generateChatResponse called with message:', text);

    const wedifyApiRequest = new WedifyApiRequest();
    const requestBody = JSON.stringify({
      message: text,
    });
    console.log('🌐 Request body prepared:', requestBody);

    console.log('🌐 Making API request to https://wedify.be/api/wedding-agent/chat with context...');
    const response = await wedifyApiRequest.makeRequest(
      'https://wedify.be/api/wedding-agent/chat?includeContext=true',
      'POST',
      requestBody as any
    );
    console.log('🌐 API request completed, response:', {
      ok: response?.ok,
      status: response?.status,
      statusText: response?.statusText,
      hasResponse: !!response
    });

    if (!response || !response.ok) {
      console.log('⚠️ API call failed with status:', response?.status);
      throw new Error(`Backend API error: ${response?.status || 'No response'}`);
    }

    console.log('🌐 Parsing JSON response...');
    const data: BackendChatResponse = await response.json();
    console.log('🌐 Backend response parsed:', {
      hasMessage: !!data.message,
      messageLength: data.message?.length,
      success: data.success,
      hasData: !!data.data,
      actionPerformed: data.actionPerformed,
      dataType: typeof data.data,
      dataKeys: data.data ? Object.keys(data.data) : null
    });

    // Convert backend response format to our expected format
    const actions: Array<{ type: 'calendar' | 'task' | 'budget' | 'vendor'; data: any; }> = [];
    
    console.log('🎯 Checking for actions in backend response...');
    if (data.actionPerformed && data.success) {
      console.log('🎯 Action performed by backend:', data.actionPerformed);
      
      // Map backend action types to our action types
      let actionType: 'calendar' | 'task' | 'budget' | 'vendor' = 'budget'; // default
      
      if (data.actionPerformed.includes('budget')) {
        actionType = 'budget';
      } else if (data.actionPerformed.includes('calendar') || data.actionPerformed.includes('event')) {
        actionType = 'calendar';
      } else if (data.actionPerformed.includes('task')) {
        actionType = 'task';
      } else if (data.actionPerformed.includes('vendor')) {
        actionType = 'vendor';
      }
      
      console.log('🎯 Mapped action type:', actionType);
      
      const actionData = {
        operation: data.actionPerformed,
        ...data.data,
        actionParameters: data.actionParameters,
        backendResponse: data
      };
      console.log('🎯 Created action data:', actionData);
      
      actions.push({
        type: actionType,
        data: actionData
      });
      console.log('🎯 Added action to actions array, total actions:', actions.length);
    } else {
      console.log('🎯 No actionPerformed or success=false, no actions created');
    }

    const finalResponse = {
      text: data.message,
      message: data.message, // Include both text and message for compatibility
      actions: actions,
      data: data.data, // Pass through backend data for search results
      actionPerformed: data.actionPerformed, // Pass through new backend format
      actionParameters: data.actionParameters // Pass through new backend parameters
    };
    console.log('🌐 Returning final response:', {
      hasText: !!finalResponse.text,
      textLength: finalResponse.text?.length,
      actionsCount: finalResponse.actions?.length || 0,
      hasData: !!finalResponse.data,
      actionPerformed: finalResponse.actionPerformed,
      actionParameters: finalResponse.actionParameters
    });
    
    return finalResponse;

  } catch (error: any) {
    console.error('❌ Backend API Error in generateChatResponse:', error);
    console.error('❌ Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      type: typeof error
    });
    
    // Fallback to mock responses only if there's a real error
    console.log('🔄 Falling back to mock responses...');
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('budget') || lowerText.includes('$') || lowerText.includes('catering')) {
      console.log('💰 API failed, providing mock budget response');
      return {
        text: "I'm having trouble connecting to the backend, but I can see you want to update your budget. Please try again in a moment, or check your budget section manually.",
        actions: [
          {
            type: 'budget',
            data: {
              operation: 'update',
              category: 'catering',
              amount: 5000,
              message: text,
              mock: true
            }
          }
        ]
      };
    }
    
    if (lowerText.includes('schedule') || lowerText.includes('appointment') || lowerText.includes('visit')) {
      console.log('📅 API failed, providing mock calendar response');
      return {
        text: "I'm having connection issues, but I can see you want to schedule something. Please try again in a moment.",
        actions: [
          {
            type: 'calendar',
            data: {
              operation: 'add',
              title: 'Venue Visit',
              date: new Date().toISOString().split('T')[0],
              time: '2:00 PM',
              message: text,
              mock: true
            }
          }
        ]
      };
    }
    
    if (lowerText.includes('task') || lowerText.includes('todo') || lowerText.includes('remind')) {
      console.log('✅ API failed, providing mock task response');
      return {
        text: "I'm experiencing connection issues, but I can see you want to create a task. Please try again in a moment.",
        actions: [
          {
            type: 'task',
            data: {
              operation: 'add',
              title: 'Wedding Task',
              category: 'planning',
              priority: 'MEDIUM',
              message: text,
              mock: true
            }
          }
        ]
      };
    }
    
    if (lowerText.includes('hello') || lowerText.includes('hi') || lowerText.includes('help')) {
      return {
        text: "Hello! I'm your wedding planning assistant. I can help you with:\n\n📅 Scheduling appointments and venue visits\n💰 Managing your wedding budget\n✅ Creating and tracking tasks\n🔍 Finding vendors\n\nWhat would you like to work on today? 💝",
        actions: []
      };
    }
    
    // Default error response
    return {
      text: "I'm experiencing some connection issues. Please try again in a moment. If the problem persists, you can manually update your planning tools.",
      actions: []
    };
  }
};